import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { API } from "../../App";
import ProfessorStudents from "./ProfessorStudents";
import { RankingDialog } from "./ProfessorDialogs";
import ProfessorFeedback from "./ProfessorFeedback";

const LIMIT = 15;

const ProfessorStudentsPage = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const [isRankingDialogOpen, setIsRankingDialogOpen] = useState(false);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [rankingSurf, setRankingSurf] = useState("");
  const [rankingBodyboard, setRankingBodyboard] = useState("");
  const [showCreateStudent, setShowCreateStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({ name: "", email: "", phone: "" });

  const debounceRef = useRef(null);

  const fetchStudents = useCallback(async (p, q) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: LIMIT });
      if (q) params.set("search", q);
      const res = await axios.get(`${API}/admin/students?${params}`, { withCredentials: true });
      const data = res.data;
      setStudents(data.students);
      setTotal(data.total);
      setPages(data.pages);
      setPage(data.page);
    } catch (err) {
      console.error("Error fetching students:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStudents(1, ""); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchStudents(1, value);
    }, 350);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pages) return;
    fetchStudents(newPage, searchQuery);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const refreshCurrentPage = () => fetchStudents(page, searchQuery);

  const openRankingDialog = (student) => {
    setSelectedStudent(student);
    setRankingSurf(student.ranking_surf || "");
    setRankingBodyboard(student.ranking_bodyboard || "");
    setIsRankingDialogOpen(true);
  };

  const openFeedbackDialog = (student) => {
    setSelectedStudent(student);
    setIsFeedbackDialogOpen(true);
  };

  const handleUpdateRanking = async (modality, ranking) => {
    try {
      await axios.patch(
        `${API}/staff/users/${selectedStudent.user_id}/ranking`,
        { modality, ranking: ranking || null },
        { withCredentials: true }
      );
      toast.success(`Ranking de ${modality} atualizado!`);
      refreshCurrentPage();
      if (modality === "surf") setRankingSurf(ranking);
      else setRankingBodyboard(ranking);
    } catch { toast.error("Erro ao atualizar ranking"); }
  };

  const handleCreateStudent = async () => {
    if (!newStudent.name.trim()) { toast.error("Nome é obrigatório"); return; }
    try {
      const res = await axios.post(`${API}/admin/students`, newStudent, { withCredentials: true });
      toast.success(`Aluno "${res.data.name}" criado`);
      setShowCreateStudent(false);
      setNewStudent({ name: "", email: "", phone: "" });
      refreshCurrentPage();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar aluno"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#3d5a63] mb-1">Alunos</h1>
          <p className="text-sm text-[#475569]">Rankings e feedback dos alunos</p>
        </div>
        <Button size="sm" onClick={() => setShowCreateStudent(true)} className="bg-[#6697a4] hover:bg-[#567f8a]" data-testid="prof-create-student-btn">
          <UserPlus className="h-4 w-4 mr-1" /> Novo Aluno
        </Button>
      </div>

      <ProfessorStudents
        students={students}
        loading={loading}
        total={total}
        page={page}
        pages={pages}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        onPageChange={handlePageChange}
        onOpenRankingDialog={openRankingDialog}
        onOpenFeedbackDialog={openFeedbackDialog}
      />

      <RankingDialog
        isOpen={isRankingDialogOpen}
        onOpenChange={setIsRankingDialogOpen}
        selectedStudent={selectedStudent}
        rankingSurf={rankingSurf}
        rankingBodyboard={rankingBodyboard}
        onUpdateRanking={handleUpdateRanking}
      />

      <ProfessorFeedback
        open={isFeedbackDialogOpen}
        onOpenChange={setIsFeedbackDialogOpen}
        student={selectedStudent}
        onSuccess={refreshCurrentPage}
        lang="pt"
      />

      <Dialog open={showCreateStudent} onOpenChange={setShowCreateStudent}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Criar Novo Aluno</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm">Nome *</Label>
              <Input value={newStudent.name} onChange={(e) => setNewStudent({...newStudent, name: e.target.value})} placeholder="Nome do aluno" data-testid="prof-new-student-name" />
            </div>
            <div>
              <Label className="text-sm">Email (opcional)</Label>
              <Input type="email" value={newStudent.email} onChange={(e) => setNewStudent({...newStudent, email: e.target.value})} placeholder="email@exemplo.com" data-testid="prof-new-student-email" />
            </div>
            <div>
              <Label className="text-sm">Telefone (opcional)</Label>
              <Input value={newStudent.phone} onChange={(e) => setNewStudent({...newStudent, phone: e.target.value})} placeholder="+351 9XX XXX XXX" data-testid="prof-new-student-phone" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateStudent(false)}>Cancelar</Button>
            <Button onClick={handleCreateStudent} className="bg-[#6697a4] hover:bg-[#567f8a]" data-testid="prof-save-new-student-btn">Criar Aluno</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfessorStudentsPage;
